import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import FormModel from './models/FormModel';
import { modifierShapeGenerator } from '../../../lib/shapes/ModifierShape';
import FormActionShape, { ParameterShape } from '../../../lib/shapes/FormActionShape';
import { createExtender } from '../../../lib/bemUtils/bem';
import Utils from './Utils';
import FormContext from './Context';
import { FormContainerModifier } from './enums/FormContainerModifier';
import { Namespace } from '../../../translation/namespace';
import { withBlockContext } from '../../blocks/BlockContext';
import { FormResponseErrorType } from './enums/FormResponseErrorType';
import Event from '../../../lib/EventEmitter';
import { parametersContainTypeFile } from './models/FormModel/utils/getParameterForType';
import { withRouter } from '../../hocs/withRouter';
import { murmurhash } from '../../../lib/js/hash';
import { InteractionStatus } from '../../../types/InteractionStatus';
import { windowLogger } from '../../../_lib/Logger/windowLogger';

export const FormTemplate = FormContext.Consumer;
export const FormModelAssigner = FormContext.Provider;

const FormExtender = createExtender('form form1');
const FormContainerExtender = createExtender('form__container');

/**
 * Encapsulates whole Form stuff
 *
 * !! needs refactoring: From does currently initialize mode and viewmodel to many times.
 *    -> once in constructor so it can render with viewmodels, also on server.
 *    -> then immediately again in componentdidmount, so subscribe() to models works.
 *
 */
class Form extends Component {
  static defaultProps = {
    children: null,
    method: 'POST',
    checkPageExists: false,
    validateResponseStatus: null,
    model: undefined,
    shouldFormModelInitialize: undefined,
    params: undefined,
    submittableWithoutChange: false,
    allowNavigationOnUnsavedChanges: false,
    // Modifier
    modifier: [],
    containerModifier: [],

    role: null,
    ariaLabel: null,
    ariaLabelledby: null,

    // Events
    onSubmit: null,
    onSuccess: null,
    beforeSubmit: null,
    onFailure: null,
  };

  constructor(props) {
    super(props);

    this.models = this.createModel([].concat(props.model));

    this.state = {
      viewModels: this.createViewModel(this.models),
    };

    this.mounted = undefined;

    // we used to generate ids based on the form url, but backend started to combine many urls and we do want to have
    this.idWithHashedUrl = `${this.props.id}-${this.getHashedUrl()}`;

    if (!this.props.id) {
      windowLogger.warn('some form did not get passed a valid ID');
    }
  }

  getHashedUrl() {
    const { model } = this.props;

    // this is, again, the ultra special case for members. the only place we have the form array model geschmuffelt.
    if (Array.isArray(model)) {
      return murmurhash(model[0]);
    }

    return murmurhash(this.props.model.url);
  }

  componentDidUpdate(prevProps) {
    const { model, shouldFormModelInitialize } = this.props;

    if (
      model?.url !== prevProps.model?.url ||
      (shouldFormModelInitialize && shouldFormModelInitialize(this.props.model, prevProps.model))
    ) {
      this.initializeModel();
    }
  }

  componentDidMount() {
    const { allowNavigationOnUnsavedChanges } = this.props;

    if (!allowNavigationOnUnsavedChanges) {
      Event.emit(Event.prompt.addToQueue, { id: this.idWithHashedUrl, isDirty: this.isDirty });
    }

    this.initializeModel();
    if (!this.mounted) {
      this.mounted = true;
    }
  }

  componentWillUnmount() {
    const { allowNavigationOnUnsavedChanges } = this.props;
    if (!allowNavigationOnUnsavedChanges) {
      Event.emit(Event.prompt.removeFromQueue, this.idWithHashedUrl);
    }
    this.mounted = false;
  }

  /** Check if any of this forms' viewModels has changes */
  isDirty = () => {
    return (
      this.state.viewModels &&
      this.state.viewModels.some(model => model && model.hasChanges() && model.status !== InteractionStatus.SUCCESS)
    );
  };

  /**
   * Standard Form Messages with ErrorTypes from Backend for validation.
   * Merges a given set of Messages with the default messages of a common form.
   */
  mapStatusMessages = (mapping) => {
    const { t } = this.props;
    const message = transkey => Utils.createStatusMessage(t(transkey));
    const messages = {
      [InteractionStatus.SUCCESS]: message('status.success'),
      [InteractionStatus.SENDING]: message('status.sending'),
      [InteractionStatus.FAIL]: message('status.fail'),
      [FormResponseErrorType.PAGE_NOT_EXISTS]: message('errorType.pageDoestNotExistYet'),
      [FormResponseErrorType.PAGE_CREATE_DUPLICATE_AREA_HOME_PAGE]: message(
        'errorType.pageCreateDuplicateAreaHomePage',
      ),
      [FormResponseErrorType.PAGE_LOCKED]: message('errorType.pageLocked'),
      [FormResponseErrorType.MISSING_BUT_REQUIRED]: message('errorType.missingButRequired'),
      [FormResponseErrorType.INVALID_HTML]: message('errorType.invalidHtml'),
      [FormResponseErrorType.INVALID_VERSION_STATE]: message('errorType.invalidVersionState'),
      [FormResponseErrorType.EXPIRED_SESSION_OR_CSRF]: message('errorType.expiredSessionOrCsrf'),
      [FormResponseErrorType.PAYLOAD_ERROR]: message('errorType.payloadError'),
      [FormResponseErrorType.GENERIC_ERROR]: message('errorType.genericError'),
      [FormResponseErrorType.INVALID_VALUE]: message('errorType.invalidValue'),
    };

    return Object.assign(messages, mapping);
  };

  /**
   * Standard Submit of Form.
   * Important: If the <Form> is initialized with an array of models,
   * This function will submit all of them
   * @param event
   */
  submit = (event) => {
    if (event) {
      event.preventDefault();
    }

    if (this.props.beforeSubmit) {
      this.props.beforeSubmit();
    }

    this.state.viewModels.forEach((viewModel) => {
      viewModel.status = InteractionStatus.SENDING;
    });

    const submitPromise = Promise.all(this.state.viewModels.map(viewModel => viewModel.model.submit()));

    submitPromise
      .then((response) => {
        // If the group members are also expanded with refetch, use map here
        if (this.props.onSuccess) {
          this.props.onSuccess(response.length > 1 ? response : response[0], this.state.viewModels);
        }
      })
      .catch(() => {
        this.state.viewModels.forEach((viewModel) => {
          viewModel.status = InteractionStatus.FAIL;
        });

        if (this.props.onFailure) {
          this.props.onFailure();
        }
      });
  };

  /**
   * Returns a single model or an array of models.
   * Important: It will subscribe to the models changes too.
   * @param modelJson
   * @returns {*}
   */
  createModel = (modelJson) => {
    if (modelJson) {
      if (Array.isArray(modelJson)) {
        return modelJson.map(this.createModel.bind(this));
      }
      const model = new FormModel({
        url: modelJson.url,
        method: modelJson.method || this.props.method,
        navigate: this.props.navigate,
        submittableWithoutChange: this.props.submittableWithoutChange,
        params: modelJson.params || this.props.params,
        name: modelJson.name,
        checkPageExists: modelJson.checkPageExists || this.props.checkPageExists,
        validateResponseStatus: modelJson.validateResponseStatus || this.props.validateResponseStatus,
      });
      model.subscribe(this.update);

      return model;
    }

    return null;
  };

  /**
   * Returns a single viewModel or an Array of them.
   * @param models
   * @returns {*}
   */
  createViewModel = (models) => {
    if (models) {
      if (Array.isArray(models)) {
        return models.map(this.createViewModel.bind(this));
      }

      // From here we can be sure, that it's a single Model
      const model = models;
      const hasStatus = model.status !== InteractionStatus.NONE;
      return {
        name: model.name,
        model,
        inputs: model.parameter,
        status: model.status,
        response: model.response,
        isValid: model.isValid(),
        hasChanges: model.hasChanges,
        submitActionEnabled: model.submitActionEnabled,
        hiddenFields: Utils.renderHiddenFields(model.parameter),
        onChange: model.changeValue,
        appendParameter: model.appendParameter,
        validateResponseStatus: model.validateResponseStatus,
        responseUrl: model.getResponseUrl(),
        getStatus: (messages, status, renderAnyway = false, inverted = false) =>
          (hasStatus || renderAnyway) &&
          Utils.renderStatusMessages(
            this.mapStatusMessages(messages),
            status || model.status,
            model.response,
            inverted,
          ),
        reset: this.initializeModel,
      };
    }
    return null;
  };

  /**
   * Will update the Form and rerender it's template by manipulating the state.
   */
  update = () => {
    if (this.mounted) {
      this.setState({
        viewModels: this.createViewModel(this.models),
      });
    }
  };

  /**
   * Creates Models and it's viewModels and adds the viewModels to the forms state.
   */
  initializeModel = () => {
    if (this.props.model) {
      const modelsFromProps = [].concat(this.props.model);

      this.models = this.createModel(modelsFromProps);

      this.setState({
        viewModels: this.createViewModel(this.models),
      });
    }
  };

  /**
   * Determines which function will be called at the forms submit.
   * @param event
   */
  onSubmit = (event) => {
    if (this.props.onSubmit) {
      this.props.onSubmit(event, this.state.viewModels.length > 1 ? this.models : this.models[0]);
    } else {
      this.submit(event);
    }
  };

  /**
   * Returns the value of the FormProvider.
   * This can be a single model, or an array of them
   * @returns {*}
   */
  determineViewModels = () => {
    if (Array.isArray(this.state.viewModels) && this.state.viewModels.length === 1) {
      return this.state.viewModels[0];
    }

    return this.state.viewModels;
  };

  formContainsFileInput() {
    return this.models.some(model => parametersContainTypeFile(model.parameter));
  }

  render() {
    return (
      <div className={FormContainerExtender(this.props.containerModifier)}>
        {this.state.viewModels && this.props.model
          ? (
              <form
                id={this.idWithHashedUrl}
                action={this.models[0].url || this.props.model.url}
                method={this.props.method || this.props.model.method}
                className={FormExtender(this.props.modifier)}
                encType={this.formContainsFileInput() ? 'multipart/form-data' : undefined}
                role={this.props.role}
                aria-label={this.props.ariaLabel}
                aria-labelledby={this.props.ariaLabelledby}
                // Events
                onSubmit={this.onSubmit}
              >
                <FormContext.Provider value={this.determineViewModels()}>{this.props.children}</FormContext.Provider>
              </form>
            )
          : (
              this.props.children
            )}
      </div>
    );
  }
}

Form.propTypes = {
  id: PropTypes.string.isRequired,
  model: PropTypes.oneOfType([
    FormActionShape,
    PropTypes.arrayOf(FormActionShape),
    PropTypes.arrayOf(PropTypes.arrayOf(FormActionShape)),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.arrayOf(FormActionShape))),
  ]),
  shouldFormModelInitialize: PropTypes.func,
  method: PropTypes.oneOf(['POST', 'GET']),
  checkPageExists: PropTypes.bool,
  validateResponseStatus: PropTypes.func,
  containerModifier: modifierShapeGenerator(FormContainerModifier),
  modifier: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  params: PropTypes.arrayOf(ParameterShape),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  submittableWithoutChange: PropTypes.bool,
  allowNavigationOnUnsavedChanges: PropTypes.bool,
  // Events
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  beforeSubmit: PropTypes.func,
  onFailure: PropTypes.func,

  navigate: PropTypes.func.isRequired,

  role: PropTypes.string,
  ariaLabel: PropTypes.string,
  ariaLabelledby: PropTypes.string,

  // Translations
  t: PropTypes.func.isRequired,
};

export default withTranslation(Namespace.Form)(withBlockContext(withRouter(Form)));
